import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";

// Site::Faq::ClickToAction::Component の Stimulus コントローラ。
// landIeul: FAQ ClickToAction の 土地の有無→都道府県→市区町村→町名→字丁目 の段階選択を再現。
// 機能は Home::ClickToAction と同一。トグルするクラスのみ FaqCta_ 接頭辞。

interface NamedItem {
  id: string;
  name: string;
  yomi: string;
}

const GROUP_MAP: [RegExp, string][] = [
  [/^[ア-オ]/, "ア"],
  [/^[カ-コガ-ゴ]/, "カ"],
  [/^[サ-ソザ-ゾ]/, "サ"],
  [/^[タ-トダ-ド]/, "タ"],
  [/^[ナ-ノ]/, "ナ"],
  [/^[ハ-ホバ-ボパ-ポ]/, "ハ"],
  [/^[マ-モ]/, "マ"],
  [/^[ヤ-ヨ]/, "ヤ"],
  [/^[ラ-ロ]/, "ラ"],
  [/^[ワ-ン]/, "ワ"],
];

function mapToGroup(yomi: string): string {
  const m = GROUP_MAP.find(([re]) => re.test(yomi));
  return m ? m[1] : "その他";
}

function groupByYomi(items: NamedItem[]): { name: string; items: NamedItem[] }[] {
  const map = new Map<string, NamedItem[]>();
  items.forEach((it) => {
    const g = mapToGroup(it.yomi);
    map.set(g, [...(map.get(g) || []), it]);
  });
  return Array.from(map.keys())
    .sort()
    .map((k) => ({
      name: k,
      items: (map.get(k) || []).sort((a, b) =>
        a.yomi < b.yomi ? -1 : a.yomi > b.yomi ? 1 : 0,
      ),
    }));
}

const GET_CITIES = `query GetCities($id: ID!){prefecture(id:$id){cities{id name yomi}}}`;
const GET_TOWNS = `query GetTowns($id: ID!){city(id:$id){towns{id name yomi}}}`;
const GET_STREETS = `query GetStreets($id: ID!){town(id:$id){streets{id name yomi}}}`;

const CURRENT = "FaqCta_current";
const DONE = "FaqCta_done";

class SiteFaqClickToActionController extends Controller {
  static targets = [
    "possession",
    "prefecture",
    "city",
    "town",
    "street",
    "group",
    "form",
    "link",
  ];
  static values = { graphql: String };

  declare readonly possessionTarget: HTMLSelectElement;
  declare readonly prefectureTarget: HTMLSelectElement;
  declare readonly cityTarget: HTMLSelectElement;
  declare readonly townTarget: HTMLSelectElement;
  declare readonly streetTarget: HTMLSelectElement;
  declare readonly groupTargets: HTMLElement[];
  declare readonly formTargets: HTMLElement[];
  declare readonly linkTarget: HTMLAnchorElement;
  declare readonly graphqlValue: string;

  private readonly LEVELS = ["possession", "prefecture", "city", "town", "street"];

  async change(event: Event): Promise<void> {
    const changed = (event.target as HTMLElement).dataset.level ||
      this.levelOf(event.target as HTMLSelectElement);

    if (changed === "possession" && this.possessionTarget.value === "") {
      this.resetFrom("prefecture");
    } else if (changed === "prefecture") {
      await this.reloadCities();
    } else if (changed === "city") {
      await this.reloadTowns();
    } else if (changed === "town") {
      await this.reloadStreets();
    }
    this.refresh();
  }

  private levelOf(select: HTMLSelectElement): string {
    if (select === this.prefectureTarget) return "prefecture";
    if (select === this.cityTarget) return "city";
    if (select === this.townTarget) return "town";
    if (select === this.streetTarget) return "street";
    return "possession";
  }

  private async fetchData(query: string, id: string): Promise<any> {
    const res = await fetch(this.graphqlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, variables: { id } }),
    });
    const json = await res.json();
    return json.data;
  }

  private optionsHtml(placeholder: string, groups: { name: string; items: NamedItem[] }[]): string {
    let html = `<option value="">${placeholder}</option>`;
    groups.forEach((g) => {
      html += `<optgroup label="${g.name}">`;
      g.items.forEach((it) => {
        html += `<option value="${it.id}">${it.name}</option>`;
      });
      html += "</optgroup>";
    });
    return html;
  }

  private async reloadCities(): Promise<void> {
    this.resetFrom("city");
    const pref = this.prefectureTarget.value;
    if (!pref) return;
    const data = await this.fetchData(GET_CITIES, pref);
    const cities: NamedItem[] = data?.prefecture?.cities || [];
    this.cityTarget.innerHTML = this.optionsHtml("市区町村", groupByYomi(cities));
  }

  private async reloadTowns(): Promise<void> {
    this.resetFrom("town");
    const city = this.cityTarget.value;
    if (!city) return;
    const data = await this.fetchData(GET_TOWNS, city);
    const towns: NamedItem[] = data?.city?.towns || [];
    this.townTarget.innerHTML = this.optionsHtml("町名", groupByYomi(towns));
  }

  private async reloadStreets(): Promise<void> {
    const town = this.townTarget.value;
    if (!town) {
      this.resetStreet();
      return;
    }
    const data = await this.fetchData(GET_STREETS, town);
    let streets: NamedItem[] = data?.town?.streets || [];
    if (streets.length === 0) streets = [{ id: "0", name: "その他", yomi: "その他" }];
    this.streetTarget.innerHTML = this.optionsHtml("字・丁目", groupByYomi(streets));
  }

  private resetFrom(level: string): void {
    const order = ["prefecture", "city", "town", "street"];
    const start = order.indexOf(level);
    order.slice(start).forEach((l) => {
      if (l === "prefecture") this.prefectureTarget.value = "";
      if (l === "city") this.cityTarget.innerHTML = '<option value="">市区町村</option>';
      if (l === "town") this.townTarget.innerHTML = '<option value="">町名</option>';
      if (l === "street") this.resetStreet();
    });
  }

  private resetStreet(): void {
    this.streetTarget.innerHTML =
      '<option value="">字・丁目</option><optgroup label="その他"><option value="0">その他</option></optgroup>';
  }

  private refresh(): void {
    const vals = [
      this.possessionTarget.value,
      this.prefectureTarget.value,
      this.cityTarget.value,
      this.townTarget.value,
      this.streetTarget.value,
    ];
    const activeIdx = vals.findIndex((v) => !v);

    this.LEVELS.forEach((level, i) => {
      const filled = !!vals[i];
      const isCurrent = i === activeIdx;
      const group = this.groupTargets.find((g) => g.dataset.level === level);
      const form = group?.querySelector<HTMLElement>(`[data-site--faq--click-to-action-target="form"]`);
      [group, form].forEach((el) => {
        if (!el) return;
        el.classList.toggle(DONE, filled);
        el.classList.toggle(CURRENT, isCurrent);
      });
    });

    const complete = activeIdx === -1;
    this.linkTarget.classList.toggle(CURRENT, complete);
    this.linkTarget.href = this.buildHref();
  }

  private buildHref(): string {
    const text = (sel: HTMLSelectElement, placeholder: string) => {
      const t = sel.selectedOptions[0]?.text || "";
      return t === placeholder ? "" : t;
    };
    const pos = this.possessionTarget.value;
    const pref = this.prefectureTarget.value;
    const city = this.cityTarget.value;
    const town = this.townTarget.value;
    const prefName = text(this.prefectureTarget, "都道府県");
    const cityName = text(this.cityTarget, "市区町村");
    const townName = text(this.townTarget, "町名");
    const streetName = text(this.streetTarget, "字・丁目");
    const base = `property-type-id=land_usage_${pos}&pref-id=${pref}&city-id=${city}&town-id=${town}&prefecture_name=${prefName}&city_name=${cityName}&town_name=${townName}&street-value=${streetName}`;
    const search = window.location.search;
    return search ? `/plan-request/${search}&${base}` : `/plan-request/?${base}`;
  }
}

application.register("site--faq--click-to-action", SiteFaqClickToActionController);
